/**
 * WHICH ARMOUR A VEHICLE PRESENTS - the rule, with no geometry in it.
 *
 * A vehicle's Front, Side and Rear values are chosen by where a shot
 * comes from. Working that out is the map's job (`battlefield/facing.ts`);
 * deciding whether to LISTEN to the map is this one's, and it deliberately
 * lives outside `engine/battlefield/` so the combat engine never acquires
 * a dependency on the map. A session with no battlefield resolves damage
 * exactly as it always did.
 *
 * THE CHOICE AND THE ANSWER ARE DIFFERENT FIELDS. `vehicleFacing` is what
 * a person decided and is stored; `vehicleFacingFromMap` is what the map
 * says for ONE attacker and is never stored - not because a guard strips
 * it, but because no writer knows its name. `stateFromTarget` builds
 * `CombatState` field by field, so the derived value cannot be persisted
 * by construction.
 *
 * That is the cover lesson (v7.51.0) applied one step earlier. Cover had
 * to be stamped `derived` and stripped because the map wrote into the
 * same field a person used; keeping them apart from the start removes
 * the need for the stamp, the strip and the shadow field all at once.
 */

/** Which armour value a hit uses. */
export type VehicleFacing = 'front' | 'side' | 'rear';

/** What a person may say, including "let the map answer". */
export type VehicleFacingChoice = VehicleFacing | 'auto';

export const VEHICLE_FACING_CHOICES: readonly VehicleFacingChoice[] = ['auto', 'front', 'side', 'rear'];

export const VEHICLE_FACING_LABELS: Record<VehicleFacingChoice, string> = {
  auto: 'Auto',
  front: 'Front',
  side: 'Side',
  rear: 'Rear',
};

export function isVehicleFacingChoice(v: unknown): v is VehicleFacingChoice {
  return typeof v === 'string' && (VEHICLE_FACING_CHOICES as readonly string[]).includes(v);
}

/**
 * The facing a hit uses: the person's, unless they left it to the map.
 *
 * Deliberately the same three lines as `resolveRangeBand` in
 * `attackCalc.ts` - both answer "the map knows, until somebody says
 * otherwise", and if these two ever stop agreeing in shape one of them
 * is wrong.
 *
 * Falls back to `front` when the map cannot answer: nobody on the map, a
 * hull nobody has turned, a shooter standing on top of it. That is
 * exactly how every session behaved before any of this existed, which is
 * what makes the fallback the safe one rather than merely a default.
 */
export function resolveVehicleFacing(
  choice: VehicleFacingChoice | null | undefined,
  fromMap: VehicleFacing | null | undefined,
): VehicleFacing {
  if (choice && choice !== 'auto') return choice;
  return fromMap || 'front';
}

/** Enough of a Target to answer. Both halves ride on the projection. */
export interface FacingCarrier {
  vehicleFacing?: VehicleFacingChoice | null;
  /** What the map says, for this attacker only. Never stored. */
  vehicleFacingFromMap?: VehicleFacing | null;
}

/**
 * The facing in play for this target, right now, for whoever is looking.
 *
 * THE ONLY CALLER of `resolveVehicleFacing` in the app. Every read site -
 * the damage calculation, the status chip, the gear tab, the body map -
 * goes through here, so none of them can disagree about which armour is
 * being presented.
 */
export function effectiveVehicleFacing(target: FacingCarrier | null | undefined): VehicleFacing {
  return resolveVehicleFacing(target?.vehicleFacing, target?.vehicleFacingFromMap);
}
